import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import {
  Card,
  Col,
  Container,
  Row,
} from 'react-bootstrap'
import { CostSummary } from './Components/CostComponent'
import { UsageSummary } from './Components/UsageComponent'
import { ESGSummary } from './Components/ESGComponent'
import {
  ActiveIssueSummary,
  LeakSummary,
  TotalIssueSummary,
} from './Components/LeakComponent'
import {
  UsageBarChart,
  UsagePieChart,
} from './Components/GraphComponent'
import { useAggregatedUsage } from '@data/waterUsage/waterUsage'

import moment from 'moment-timezone'
import {
  AggUsageQuery,
  TrendData,
} from '@data/waterUsage/model/usageStats.model'
import Device from '@data/device/model/device'
import {
  useDevicesLoading,
  useVisibleDevices,
} from '@data/device/device'
import {
  useAccountsQuery,
  useSelectedAccounts,
} from '@data/accounts/accounts'
import { useAuthStore } from '@store/auth.store'
import HomeTacticalEmailModal from './HomeTacticalEmailModal'
import SubscriptionAlerts from '@components/main/Subscriptions/SubscriptionAlerts'
import BillingOverview from '@components/main/Subscriptions/BillingOverview'
import { useSubscriptionDevicesMulti } from '@data/subscriptions/subscriptions'
import { useNavigate } from 'react-router-dom'
import { useCurrentUserQuery } from '@data/currentUser/currentUser'
import { useAccountUsersQuery } from '@data/users/users'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { useActiveAlertsQuery } from '@data/alerts/alerts'
import '@components/main/Subscriptions/Subscriptions.scss'

function HomeTactical() {
  const devices = useVisibleDevices()
  const loadingDevices = useDevicesLoading()
  const { data: queue = [] } = useActiveAlertsQuery()
  const selectedAccounts = useSelectedAccounts()
  const { isLoading: isLoadingAccounts } = useAccountsQuery()
  const permissions = useAuthStore((state) => state.permissions)
  const { data: userInfo } = useCurrentUserQuery()

  const userUom = userInfo?.preferences?.uom

  const navigate = useNavigate()
  const selectedAccountIds = selectedAccounts.map((a) => Number(a.id))
  const singleAccountId = selectedAccountIds.length === 1 ? selectedAccountIds[0] : undefined
  const { data: subscriptionDevices } = useSubscriptionDevicesMulti(selectedAccountIds)

  const inputRef = useRef(null)

  const [isBuildingReport, setIsBuildingReport] = useState<boolean>(false)
  const [filteredAggData, setFilteredAggData] = useState<any>({})
  const [displaySendEmailModal, setDisplaySendEmailModal] = useState(false)
  const [pdfBase64, setPdfBase64] = useState('')
  const [trendData, setTrendData] = useState<TrendData>({
    total: 0,
    currMonth: 0,
    prevMonth: 0,
    currMonthHot: null,
    prevMonthHot: null,
    currMonthCold: null,
    prevMonthCold: null,
    minMonth: null,
    maxMonth: null,
    maxMinDiff: 0,
    maxMinDiffCurrency: {},
  })
  const emailAccountId = displaySendEmailModal ? selectedAccounts[0]?.id ?? null : null
  const { data: accountUsers = [] } = useAccountUsersQuery(emailAccountId, displaySendEmailModal)

  const utcDate = moment.utc()
  const endDate: string = utcDate.format('YYYY-MM-DDTHH:mm:ss')
  const startDate: string = utcDate
    .subtract(12, 'months')
    .month(0)
    .date(1)
    .hour(0)
    .minute(0)
    .second(0)
    .format('YYYY-MM-DDTHH:mm:ss')
  const queryParams: AggUsageQuery = {
    startDate: startDate,
    endDate: endDate,
    aggType: 'Month',
    uom: userUom,
  }

  const { data: aggStats, isLoading, isFetching } = useAggregatedUsage(queryParams)

  // Gates the PDF export buttons, which rasterise the whole card. The aggregated usage
  // feeds the usage, ESG and leak tiles and both charts, so it is the closest single
  // signal for "the page is ready to capture". Costs are deliberately not here: the
  // request is owned by `CostSummary`, and duplicating the hook only fed this flag.
  const isLoadingData = isLoadingAccounts || loadingDevices || isLoading || isFetching

  const dUuidSet = useMemo(() => new Set(devices.map((d) => d.dUuid)), [devices])

  const filteredQueue = useMemo(
    () => queue.filter((q) => dUuidSet.has(q.dUuid)),
    [queue, dUuidSet],
  )

  useEffect(() => {
    if (aggStats && !isFetching && !isLoading) {
      if (Object.keys(aggStats).length === 0) return

      const filteredData = Object.keys(aggStats)
        .filter((dUuid) => {
          const d: Device | undefined = devices.find(
            (d: Device) => d.dUuid === dUuid && !d.deviceSettings.masterDeviceIdRef, //ignore sub meters data
          )
          if (d) return dUuid
        })
        .reduce((cur, key) => {
          return Object.assign(cur, { [key]: aggStats[key] })
        }, {})
      setFilteredAggData(filteredData)
    }
  }, [devices, aggStats, isFetching, isLoading])

  useEffect(() => {
    const currentYear: number = moment.utc().year()
    const previousMonth: number = moment.utc().month()
    const currentMonth: number = moment.utc().month() + 1

    const trendData: TrendData = Object.keys(filteredAggData).reduce(
      (acc: TrendData, currentValue: string) => {
        const device = devices.find((d) => d.dUuid === currentValue)
        const yearData: { [key: string]: { [key: string]: number } } = filteredAggData[currentValue]
        const yearKeys: string[] = Object.keys(yearData)
        yearKeys.forEach((year) => {
          const monthData: { [key: string]: number } = yearData[year]
          const yearTotal: TrendData = Object.keys(monthData).reduce(
            (acc: TrendData, monthNum: string) => {
              const monthValue = monthData[monthNum]
              if (year === currentYear.toString()) {
                if (acc.maxMonth === null || monthValue > acc.maxMonth) {
                  acc.maxMonth = monthValue
                }

                if (acc.minMonth === null || monthValue < acc.minMonth) {
                  acc.minMonth = monthValue
                }
              }

              return {
                ...acc,
                total: acc.total + monthValue,
                currMonth:
                  year === currentYear.toString() && monthNum === currentMonth.toString()
                    ? acc.currMonth + monthValue
                    : acc.currMonth,
                prevMonth:
                  year === currentYear.toString() && monthNum === previousMonth.toString()
                    ? acc.prevMonth + monthValue
                    : acc.prevMonth,
                currMonthHot:
                  year === currentYear.toString() &&
                  monthNum === currentMonth.toString() &&
                  device?.deviceSettings.hot
                    ? acc.currMonthHot === null
                      ? monthValue
                      : acc.currMonthHot + monthValue
                    : acc.currMonthHot,
                prevMonthHot:
                  year === currentYear.toString() &&
                  monthNum === previousMonth.toString() &&
                  device?.deviceSettings.hot
                    ? acc.prevMonthHot === null
                      ? monthValue
                      : acc.prevMonthHot + monthValue
                    : acc.prevMonthHot,
                currMonthCold:
                  year === currentYear.toString() &&
                  monthNum === currentMonth.toString() &&
                  !device?.deviceSettings.hot
                    ? acc.currMonthCold === null
                      ? monthValue
                      : acc.currMonthCold + monthValue
                    : acc.currMonthHot,
                prevMonthCold:
                  year === currentYear.toString() &&
                  monthNum === previousMonth.toString() &&
                  !device?.deviceSettings.hot
                    ? acc.prevMonthCold === null
                      ? monthValue
                      : acc.prevMonthCold + monthValue
                    : acc.prevMonthCold,
              }
            },
            {
              total: 0,
              currMonth: 0,
              prevMonth: 0,
              currMonthHot: null,
              prevMonthHot: null,
              currMonthCold: null,
              prevMonthCold: null,
              maxMonth: null,
              minMonth: null,
              maxMinDiff: 0,
              maxMinDiffCurrency: {},
            },
          )
          if (yearTotal.minMonth && yearTotal.maxMonth) {
            const diff = (yearTotal.maxMonth - yearTotal.minMonth) / 1000
            acc.maxMinDiff += diff

            if (device && !acc.maxMinDiffCurrency[device?.deviceSettings.currency]) {
              acc.maxMinDiffCurrency[device?.deviceSettings.currency] =
                diff * device.deviceSettings.cost
            } else if (device && acc.maxMinDiffCurrency[device?.deviceSettings.currency]) {
              acc.maxMinDiffCurrency[device?.deviceSettings.currency] +=
                diff * device.deviceSettings.cost
            }
          }

          acc.total += yearTotal.total
          acc.prevMonth += yearTotal.prevMonth
          acc.currMonth += yearTotal.currMonth

          acc.currMonthHot =
            acc.currMonthHot && yearTotal.currMonthHot
              ? acc.currMonthHot + yearTotal.currMonthHot
              : yearTotal.currMonthHot === null
                ? acc.currMonthHot
                : yearTotal.currMonthHot
          acc.prevMonthHot =
            acc.prevMonthHot && yearTotal.prevMonthHot
              ? acc.prevMonthHot + yearTotal.prevMonthHot
              : yearTotal.prevMonthHot === null
                ? acc.prevMonthHot
                : yearTotal.prevMonthHot

          acc.currMonthCold =
            acc.currMonthCold && yearTotal.currMonthCold
              ? acc.currMonthCold + yearTotal.currMonthCold
              : yearTotal.currMonthCold === null
                ? acc.currMonthCold
                : yearTotal.currMonthCold
          acc.prevMonthCold =
            acc.prevMonthCold && yearTotal.prevMonthCold
              ? acc.prevMonthCold + yearTotal.prevMonthCold
              : yearTotal.prevMonthCold === null
                ? acc.prevMonthCold
                : yearTotal.prevMonthCold
          acc.minMonth = yearTotal.minMonth
          acc.maxMonth = yearTotal.maxMonth
        })
        return acc
      },
      {
        total: 0,
        currMonth: 0,
        prevMonth: 0,
        currMonthHot: null,
        prevMonthHot: null,
        currMonthCold: null,
        prevMonthCold: null,
        maxMonth: null,
        minMonth: null,
        maxMinDiff: 0,
        maxMinDiffCurrency: {},
      },
    )
    setTrendData(trendData)
  }, [filteredAggData])

  const exportPageAsPdf = (sendByEmail = false) => {
    if (inputRef.current) {
      html2canvas(inputRef.current).then((canvas) => {
        const imgData = canvas.toDataURL('image/png')
        const smartFlowLogo = new Image()
        smartFlowLogo.src = '/logo.png'
        const accountName = selectedAccounts[0].accountName

        const pdf = new jsPDF('landscape', 'mm', [297, 210])

        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()

        const widthRatio = pageWidth / canvas.width
        const heightRatio = pageHeight / canvas.height
        const ratio = widthRatio > heightRatio ? heightRatio : widthRatio

        const canvasWidth = canvas.width * ratio
        const canvasHeight = canvas.height * ratio

        const marginX = (pageWidth - canvasWidth) / 2
        const marginY = (pageHeight - canvasHeight) / 2

        pdf.text(accountName, 5, 10)
        pdf.addImage(smartFlowLogo, 'PNG', pageWidth - 60, 1, 50, 15, undefined, 'FAST')
        pdf.addImage(
          imgData,
          'JPEG',
          marginX + 10,
          marginY + 15,
          canvasWidth - 20,
          canvasHeight - 15,
          undefined,
          'FAST',
        )
        if (!sendByEmail) {
          pdf.save(
            `${accountName} - SMART FLOW year to date summary ${moment().format('DD-MM-YYYY')}.pdf`,
          )
        } else {
          setPdfBase64(pdf.output('datauristring'))
          setDisplaySendEmailModal(true)
        }
      })
    }
  }

  return (
    <div className='home'>
      {selectedAccounts.length === 1 && (
        <div className={'d-flex justify-content-end mb-3 d-grid gap-3'}>
          <button
            className='btn btn-primary'
            onClick={() => exportPageAsPdf()}
            disabled={isLoadingData || isBuildingReport}
          >
            {isBuildingReport ? (
              <div className='spinner-border spinner-border-sm '>
                <span className='sr-only'>Loading...</span>
              </div>
            ) : (
              'Export page as PDF'
            )}
          </button>
          {hasPermissions(permissions, ['ACCOUNT:ADMIN:SMARTFLOW']) && (
            <button
              className='btn btn-primary'
              onClick={() => exportPageAsPdf(true)}
              disabled={isLoadingData || isBuildingReport}
            >
              {isBuildingReport ? (
                <div className='spinner-border spinner-border-sm '>
                  <span className='sr-only'>Loading...</span>
                </div>
              ) : (
                'Send report by email'
              )}
            </button>
          )}
        </div>
      )}
      {selectedAccountIds.length > 0 && (
        <SubscriptionAlerts
          devices={subscriptionDevices}
          accountId={singleAccountId}
          onViewInvoices={() => navigate('/subscriptions')}
        />
      )}
      {selectedAccountIds.length > 0 && (
        <BillingOverview devices={subscriptionDevices} />
      )}
      <Card ref={inputRef}>
        <Card.Header>
          <Container fluid>
            <Row>
              <Col md={6}>
                <div className={'d-flex justify-content-center'}>
                  <h3>Year To Date Summary</h3>
                </div>
              </Col>
              <Col md={6}>
                <div className={'d-flex justify-content-center'}>
                  <h3>{moment().format('dddd, MMMM Do YYYY')}</h3>
                </div>
              </Col>
            </Row>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container fluid>
            <Row>
              <Col lg='auto' className={'mx-auto mt-sm-1 mb-3 mb-md-0'}>
                <CostSummary />
              </Col>
              <Col lg='auto' className={'mx-auto mt-sm-1 mb-3 mb-md-0'}>
                <UsageSummary filteredAggStats={filteredAggData} trendData={trendData} />
              </Col>
              <Col lg='auto' className={'mx-auto mt-sm-1 mb-3 mb-md-0'}>
                <ESGSummary trendData={trendData} />
              </Col>
              <Col lg='auto' className={'mx-auto mt-sm-1 mb-3 mb-md-0'}>
                <LeakSummary trendData={trendData} />
              </Col>
              <Col lg='auto' className={'mx-auto mt-sm-1 mb-3 mb-md-0'}>
                <TotalIssueSummary />
              </Col>
              <Col lg='auto' className={'mx-auto mt-sm-1 mb-3 mb-md-0'}>
                <ActiveIssueSummary filteredQueue={filteredQueue} />
              </Col>
            </Row>
            <Row className={'py-3'}>
              <Col lg={8} className={'mx-auto mt-sm-1 mb-3 mb-md-0'}>
                <UsageBarChart filteredAggStats={filteredAggData} />
              </Col>
              <Col lg={4}>
                <UsagePieChart filteredAggStats={filteredAggData} />
              </Col>
            </Row>
          </Container>
        </Card.Body>
      </Card>

      {displaySendEmailModal && (
        <HomeTacticalEmailModal
          onHide={() => setDisplaySendEmailModal(false)}
          selectedAccount={selectedAccounts[0]}
          pdfAttachment={pdfBase64}
          accountUsers={accountUsers}
        />
      )}
    </div>
  )
}

export default HomeTactical
